/**
 * Attribute review page: URL parsing and default context.
 * Business logic lives in hooks/useAttributeReview.
 */

import { toFilterId } from "./utils";

export const EMPTY_CONTEXT = {
  skuId: "—",
  mappingRunId: "—",
  modelVersion: "—",
  sourceMarketplace: "—",
  targetMarketplace: "—",
  productType: "—",
  totalAttributes: 0,
  needsReview: 0,
  approved: 0,
} as const;

export interface AttributeReviewUrlFilters {
  marketplace: string | null;
  productType: string | null;
}

export function getInitialFiltersFromUrl(
  searchParams: URLSearchParams,
): AttributeReviewUrlFilters {
  const marketplace = searchParams.get("marketplace")?.trim() || null;
  const productType = searchParams.get("productType")?.trim() || null;
  return { marketplace, productType };
}

export function toAttributeReviewFilterSlug(value: string): string {
  return toFilterId(value);
}

const attributeReviewFilterSlugAliases: Record<string, string[]> = {
  saree: ["sari", "saris"],
  sari: ["saree", "saris"],
  saris: ["saree", "sari"],
};

function expandAttributeReviewFilterSlug(slug: string): string[] {
  const variants = new Set<string>([slug]);
  if (slug.endsWith("ies")) variants.add(`${slug.slice(0, -3)}y`);
  if (slug.endsWith("s") && slug.length > 1) variants.add(slug.slice(0, -1));
  attributeReviewFilterSlugAliases[slug]?.forEach((alias) =>
    variants.add(alias),
  );
  return Array.from(variants);
}

function getAttributeReviewFilterSlugs(value: string): string[] {
  const slugs = new Set<string>();
  const addValue = (candidate: string) => {
    const slug = toAttributeReviewFilterSlug(candidate);
    expandAttributeReviewFilterSlug(slug).forEach((variant) =>
      slugs.add(variant),
    );
  };

  addValue(value);
  const terminalSegment = value
    .replace(/--+/g, ">")
    .split(">")
    .map((segment) => segment.trim())
    .filter(Boolean)
    .at(-1);
  if (terminalSegment) addValue(terminalSegment);
  return Array.from(slugs);
}

export function resolveAttributeReviewFilterValue(
  options: Array<{ name: string }>,
  value: string | null | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  const exactMatch = options.find(
    (option) => option.name.trim().toLowerCase() === normalized,
  );
  if (exactMatch) return exactMatch.name;

  const slugs = getAttributeReviewFilterSlugs(trimmed);
  const slugMatch = options.find((option) =>
    getAttributeReviewFilterSlugs(option.name).some((slug) =>
      slugs.includes(slug),
    ),
  );
  return slugMatch?.name ?? null;
}
